import React, { useEffect, useMemo, useState } from 'react';
import { apiPost } from '../../utils/api';

// 네트 역산: 항목 하나(계약외 수당·공제)의 금액을 움직여 차인지급액을 목표액에 맞춘다.
// 단계를 쌓을 수 있다 — 앞 단계가 찾은 금액은 그대로 두고, 다음 단계가 다른 항목으로 차인지급액을 다시 맞춘다.
// 여기서 짠 단계는 [적용]을 눌러야 저장된다. 그 전에는 예상 결과만 보여 준다.

const won = (v) => Math.round(v).toLocaleString('ko-KR');
const signed = (v) => `${v > 0 ? '+' : ''}${won(v)}`;
const parseAmount = (text) => {
  const digits = String(text).replace(/[^\d-]/g, '');
  return digits === '' || digits === '-' || Number.isNaN(Number(digits)) ? null : Number(digits);
};
// 입력하는 동안 자릿수를 알아보기 쉽게 쉼표를 넣는다: "3000000" → "3,000,000", "-" 는 그대로 둔다
const withCommas = (text) => {
  const negative = String(text).trim().startsWith('-');
  const digits = String(text).replace(/\D/g, '');
  if (!digits) return negative ? '-' : '';
  return `${negative ? '-' : ''}${Number(digits).toLocaleString('ko-KR')}`;
};

function NetSolverPanel({ base, row, options, initialKey, onApply, onClose }) {
  const applied = useMemo(() => row.values.net_steps || [], [row.values.net_steps]);
  const [steps, setSteps] = useState(applied);            // 쌓아 둔 단계 [{key, target}]
  const [itemKey, setItemKey] = useState(options.some((o) => o.value === initialKey) ? initialKey : (options[0] || {}).value);
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const target = parseAmount(text);

  // 입력 중인 단계도 맨 위에 쌓인 것으로 보고 예상 결과를 낸다
  const pending = target != null && itemKey ? { key: itemKey, target } : null;
  const effective = useMemo(() => (pending ? [...steps, pending] : steps), [steps, pending && pending.key, pending && pending.target]); // eslint-disable-line react-hooks/exhaustive-deps
  const effectiveJson = JSON.stringify(effective);
  const changed = effectiveJson !== JSON.stringify(applied);

  // 입력이 잠시 멈추면 예상 결과를 불러온다
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await apiPost(`${base}/net-preview`, { row_id: row.id, steps: JSON.parse(effectiveJson) });
        if (!cancelled) { setPreview(result); setError(''); }
      } catch (e) {
        if (!cancelled) { setPreview(null); setError(e.message); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [base, row.id, effectiveJson]);

  const labelOf = (key) => (options.find((o) => o.value === key) || {}).label || '(지워진 항목)';
  const groups = [...new Set(options.map((o) => o.group))];
  const solved = preview && preview.steps.length === effective.length ? preview.steps : [];
  const stack = () => { if (!pending) return; setSteps([...steps, pending]); setText(''); };

  return (
    <div className="pr-net">
      <div className="pr-net-head">
        <b>네트 역산</b>
        <button type="button" className="pr-net-close" onClick={onClose} title="닫기">✕</button>
      </div>
      <div className="pr-net-who">
        <span>{row.values.name || `${row.order}번`}</span>
        <span>네트 없이 차인 <b>{preview ? won(preview.before_net) : '…'}</b></span>
      </div>

      <div className="pr-net-section">
        <span>쌓인 단계 <em>위에서부터 차례로 적용</em></span>
        <button type="button" className="pr-net-link" disabled={!steps.length} onClick={() => setSteps([])} title="쌓인 단계를 모두 없앱니다. [적용]을 눌러야 저장됩니다">모두 없애기</button>
      </div>
      {effective.length === 0 && <div className="pr-net-hint">쌓인 단계가 없습니다. 아래에서 조정할 항목과 목표 차인지급액을 넣으세요.</div>}
      <ol className="pr-net-stack">
        {effective.map((step, i) => {
          const isPending = i >= steps.length;
          const result = solved[i];
          return (
            <li key={`${i}-${step.key}`} className={isPending ? 'pending' : ''}>
              <span className="pr-net-no">{i + 1}</span>
              <span className="pr-net-step">
                <span><b>{labelOf(step.key)}</b> 을(를) 움직여 차인 <b>{won(step.target)}</b></span>
                <span className="pr-net-step-sub">
                  {isPending && '입력 중 · '}
                  {result ? `조정 후 금액 ${won(result.amount)}` : '계산 중…'}
                  {result && !result.exact && <i> · 목표와 {signed(result.residual)}원 차이</i>}
                </span>
              </span>
              {!isPending && (
                <button type="button" className="pr-net-remove" title="이 단계만 없애기 (남은 단계는 처음부터 다시 계산됩니다)" onClick={() => setSteps(steps.filter((_, n) => n !== i))}>✕</button>
              )}
            </li>
          );
        })}
      </ol>

      <div className="pr-net-section"><span>{steps.length ? '단계 더 쌓기' : '단계 추가'}</span></div>
      <div className="pr-net-add">
        <select value={itemKey || ''} onChange={(e) => setItemKey(e.target.value)} title="금액을 움직일 항목">
          {groups.map((g) => (
            <optgroup key={g} label={g}>
              {options.filter((o) => o.group === g).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </optgroup>
          ))}
        </select>
        <input
          autoFocus
          inputMode="numeric"
          value={text}
          placeholder={`목표 차인지급액 (지금 ${preview ? won(preview.net) : '-'})`}
          onChange={(e) => setText(withCommas(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter') stack();
            e.stopPropagation();
          }}
        />
        <button type="button" className="pr-btn" disabled={!pending} onClick={stack} title="이 단계를 쌓고 다음 단계를 이어서 넣습니다 (Enter)">+ 쌓기</button>
      </div>
      {error && <div className="pr-net-warn">{error}</div>}

      {preview && effective.length > 0 && (
        <>
          <table className="pr-net-table">
            <thead><tr><th>항목</th><th>네트 없이</th><th>적용 후</th><th>변화</th></tr></thead>
            <tbody>
              {preview.lines.map((line) => {
                const diff = line.after - line.before;
                return (
                  <tr key={`${line.kind}-${line.label}`} className={`kind-${line.kind} ${Math.abs(diff) >= 0.5 ? 'changed' : ''}`}>
                    <td>{line.label}</td>
                    <td>{won(line.before)}</td>
                    <td>{won(line.after)}</td>
                    <td>{Math.abs(diff) < 0.5 ? '–' : signed(diff)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {solved.some((s) => s.amount < 0) && (
            <div className="pr-net-warn">조정 후 금액이 <b>음수</b>인 항목이 있습니다. 수당이면 급여에서 그만큼 차감, 공제면 그만큼 돌려주는 것입니다.</div>
          )}
          {solved.length > 0 && (solved[solved.length - 1].exact ? (
            <div className="pr-net-ok">마지막 목표액에 정확히 맞습니다.</div>
          ) : (
            <div className="pr-net-warn">정확히 맞는 금액이 없어 가장 가까운 값입니다 (목표와 {signed(solved[solved.length - 1].residual)}원 차이).</div>
          ))}
        </>
      )}

      <div className="pr-net-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>취소</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={!changed || loading || !preview} onClick={() => onApply(effective)}>
          {loading ? '계산 중…' : effective.length ? `적용 (${effective.length}단계)` : '네트 모두 해제'}
        </button>
      </div>
    </div>
  );
}

export default NetSolverPanel;
